import type { PageMetaFragment } from '@graphql/fragments/PageMetaFragment.graphql'
import type { PageMetaHookOptions } from '@graphql/hooks/pageMeta'
import { usePageMeta } from '@graphql/hooks/pageMeta'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { useClickTracker } from '@lib/store/tracker/ClickTracker'
import { LocalLink } from '@shared/util/link'
import { personalizeMeta } from '@shared/util/personalization'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { generateNextSeo } from 'next-seo/pages'
import type { FunctionComponent } from 'react'

export const PageMeta: FunctionComponent<PageMetaProps> = ({
    id,
    meta,
    ...restProps
}) => {
    return meta?.title ? (
        <PageMetaResolved id={id} meta={meta} {...restProps} />
    ) : (
        <SelfFetchingPageMeta id={id} {...restProps} />
    )
}

export const SelfFetchingPageMeta: FunctionComponent<
    Omit<PageMetaProps, 'meta'>
> = ({ id, ...restProps }) => {
    const { meta } = usePageMeta({ id })

    return <PageMetaResolved id={id} meta={meta} {...restProps} />
}

/**
 * Sets the contents of the page's <title> and various <meta> tags
 */
export const PageMetaResolved: FunctionComponent<PageMetaProps> = ({
    canonical,
    ogImage: overrideOgImage,
    feature,
    meta,
}) => {
    const router = useRouter()
    const personalizations = usePersonalizations()
    const personalizedMeta = personalizeMeta(meta, personalizations)

    useClickTracker({ feature })

    if (!personalizedMeta) return null

    const {
        title,
        description,
        isIndexed,
        canonicalUrl,
        twitterCard,
        twitterCreator,
        twitterSite,
        twitterImage,
        ogTitle,
        ogDescription,
    } = personalizedMeta

    const ogImage =
        overrideOgImage?.serialize(undefined, { canonical: true }) ??
        personalizedMeta.ogImage?.source

    const url =
        canonicalUrl ??
        (canonical ?? new LocalLink({ base: router.asPath })).serialize(
            undefined,
            { canonical: true },
        )

    return (
        <Head>
            {generateNextSeo({
                titleTemplate:
                    router.pathname === '/' ? undefined : '%s | Step',
                title,
                description,
                noindex: !isIndexed,
                nofollow: !isIndexed,
                canonical: url,
                twitter: {
                    cardType: twitterCard,
                    handle: twitterCreator,
                    site: twitterSite,
                },
                openGraph: {
                    title: ogTitle,
                    description: ogDescription,
                    images: ogImage ? [{ url: ogImage }] : [],
                    videos: personalizedMeta?.ogVideo?.source
                        ? [
                              {
                                  url: personalizedMeta.ogVideo.source,
                                  secureUrl: personalizedMeta.ogVideo.source,
                                  type: personalizedMeta.ogVideo.contentType,
                                  width: personalizedMeta.ogVideo.width ?? 1200,
                                  height:
                                      personalizedMeta.ogVideo.height ?? 630,
                              },
                          ]
                        : [],
                },
                additionalMetaTags: twitterImage?.source
                    ? [
                          {
                              name: 'twitter:image',
                              content: twitterImage.source,
                          },
                      ]
                    : [],
            })}
        </Head>
    )
}

export type PageMetaProps = PageMetaHookOptions & {
    /** Name of the page for the analytics */
    name: string

    /** Additional page params*/
    params?: Record<string, any>

    /** Custom canonical of the page */
    canonical?: LocalLink

    /** Tracked feature */
    feature?: string

    /** Custom og image of the page */
    ogImage?: LocalLink

    /** Page Meta data */
    meta?: PageMetaFragment
}
